import { ELEMENT_OF, ELEMENT_STYLE, type ElementId } from '../data/elements';
import { FAMILY_BY_ID } from '../data/families';

/** Shared, overhead art for collection SVGs and animated battlefield Canvas drawings.
 * Local forward is -Y. All shapes fit a 72px square, including rank-five equipment.
 * Color communicates element; silhouette communicates role; fittings communicate rank.
 */
type Piece = { d: string; fill: string; stroke: string; width: number };
export interface ArtPose { time?: number; walk?: number; attack?: number; recoil?: number }
interface Palette { ink: string; dark: string; body: string; light: string; hot: string }
const PALETTES: Record<ElementId, Palette> = {
  fire: { ink: '#341918', dark: '#76302b', body: '#e95832', light: '#ffac4b', hot: '#fff1a0' },
  water: { ink: '#162b4c', dark: '#285a8c', body: '#47a8d5', light: '#a5e7f5', hot: '#f0fdff' },
  earth: { ink: '#292e20', dark: '#66513a', body: '#92a956', light: '#d7cb91', hot: '#eff6b0' },
  wind: { ink: '#263348', dark: '#526883', body: '#9bb2ca', light: '#dce8f0', hot: '#ffffff' },
};
export const BOSS_ELEMENT: Record<string, ElementId> = { warden: 'earth', splitter: 'fire', siegebreaker: 'wind', pyreheart: 'fire', groveward: 'earth', frostjaw: 'water', siege_engine: 'fire' };
export function artPalette(id: string): Palette { return PALETTES[ELEMENT_OF[id] ?? BOSS_ELEMENT[id] ?? 'wind']; }
function level(rank: number): number { return Math.max(1, Math.min(5, Math.floor(rank))); }
function rankScale(rank: number): number { return .8 + level(rank) * .05; }

function design(id: string, rank: number, minion: boolean, pose: ArtPose = {}): Piece[] {
  const p = artPalette(id), r = level(rank), time = pose.time ?? 0;
  const walk = pose.walk ?? 0, hit = pose.attack ?? 0, recoil = pose.recoil ?? 0;
  const result: Piece[] = [];
  const path = (d: string, fill: string, stroke = p.ink, width = 1.6) => result.push({ d, fill, stroke, width });
  const poly = (points: number[], fill: string, stroke = p.ink, width = 1.6) =>
    path(points.map((v, i) => `${i % 2 ? ',' : i ? ' L' : 'M'}${v}`).join('') + ' Z', fill, stroke, width);
  const ellipse = (x: number, y: number, rx: number, ry: number, fill: string, stroke = p.ink, width = 1.6) =>
    path(`M${x-rx},${y} a${rx},${ry} 0 1,0 ${2*rx},0 a${rx},${ry} 0 1,0 ${-2*rx},0`, fill, stroke, width);
  const box = (x: number, y: number, w: number, h: number, fill: string, stroke = p.ink) =>
    path(`M${x+2},${y} h${w-4} q2,0 2,2 v${h-4} q0,2 -2,2 h${4-w} q-2,0 -2,-2 v${4-h} q0,-2 2,-2 Z`, fill, stroke);
  const line = (x: number, y: number, x2: number, y2: number, color: string, width: number) => path(`M${x},${y} L${x2},${y2}`, 'none', color, width);
  const gem = (x: number, y: number, size: number) => {
    poly([x,y-size,x+size*.7,y,x,y+size,x-size*.7,y],p.light);
    poly([x,y-size,x,y+size,x-size*.7,y],p.hot,'none');
  };
  const feather = (x: number, y: number, side: number, len: number) => {
    path(`M${x},${y+5} Q${x+side*len},${y+5} ${x+side*(len+2)},${y-12} Q${x+side*4},${y-6} ${x},${y-3} Z`,p.light);
    line(x,y+2,x+side*(len-1),y-8,p.dark,1);
  };
  const flame = (x: number, y: number, size: number) => {
    const sway = Math.sin(time*5+x)*size*.12;
    path(`M${x},${y+size*.5} Q${x-size},${y} ${x-size*.4},${y-size*.8} L${x-size*.05},${y-size*.35} L${x+sway},${y-size*1.5} Q${x+size},${y-size*.1} ${x},${y+size*.5} Z`,p.body);
    poly([x,y-size*.65,x+size*.3,y,x,y+size*.25,x-size*.25,y],p.hot,'none');
  };
  const leaf = (x: number, y: number, side: number, size: number) => {
    path(`M${x},${y+size*.5} Q${x-side*size*.9},${y-size*.5} ${x+side*size},${y-size} Q${x+side*size},${y+size*.3} ${x},${y+size*.5} Z`,p.body);
    line(x,y+size*.3,x+side*size*.65,y-size*.65,p.light,1.2);
  };
  const rarity = FAMILY_BY_ID[id]?.rarity ?? 'common';
  const prestige = { common: 0, rare: 1, epic: 2, legendary: 3 }[rarity];

  if (BOSS_ELEMENT[id]) {
    // Bosses share the roster's materials, but use broad creature silhouettes.
    for(const s of [-1,1]) {
      ellipse(s*12,15+s*walk*3,7,9,p.dark);
      ellipse(s*21,2-s*walk*2-hit*4,8,12,p.body);
      line(s*22,-2-s*walk*2-hit*4,s*22,7-s*walk*2-hit*4,p.light,3);
    }
    ellipse(0,3,19,23,p.ink); ellipse(0,0,16,20,p.body);
    path('M-12,-9 Q0,-22 12,-9','none',p.light,3);
    ellipse(0,-16,10,10,p.dark); gem(0,-17,7);
    if(id==='warden') {
      for(const s of [-1,1]) { box(s*22-7,-13,14,22,p.dark); box(s*22-5,-12,10,13,p.light); }
      box(-16,5,32,17,p.dark); gem(0,12,8);
      for(const x of [-12,-3,6]) box(x,-28,6,9,p.light);
    } else if(id==='splitter') {
      for(const s of [-1,1]) poly([s*18,7,s*32,-27-hit*3,s*23,-23,s*13,-1],p.light);
      for(const s of [-1,1]) flame(s*10,11,7);
      poly([-8,-21,-15,-32,-3,-25,0,-29,3,-25,15,-32,8,-21],p.body);
    } else if(id==='siegebreaker') {
      for(const s of [-1,1]) { feather(s*12,10,s,17); box(s*24-6,-23-hit*5,12,16,p.light); }
      box(-9,-10,18,23,p.dark); gem(0,0,8);
      poly([-8,-20,0,-33,8,-20],p.light);
    } else if(id==='pyreheart'||id==='siege_engine') {
      ellipse(0,3,13,15,p.dark); flame(0,0,14);
      for(const s of [-1,1]) { flame(s*23,-8,9); poly([s*7,-22,s*18,-32,s*16,-18],p.light); }
    } else if(id==='groveward') {
      for(const s of [-1,1]) { leaf(s*12,1,s,17); leaf(s*10,16,s,13); line(s*6,-22,s*17,-31,p.dark,4); line(s*15,-29,s*24,-27,p.dark,3); }
      gem(0,5,8); leaf(0,-14,1,10);
    } else if(id==='frostjaw') {
      for(const s of [-1,1]) { poly([s*13,4,s*31,-9,s*24,-20,s*12,-10],p.light); poly([s*7,-18,s*10,-31,s*2,-24],p.light); }
      poly([-10,-19,0,-32,10,-19,7,-10,-7,-10],p.body);
      for(const s of [-1,1]) poly([s*5,-25,s*8,-18,s*3,-18],p.hot);
      poly([-7,15,0,31,7,15],p.light);
    }
    return result;
  }
  if (!minion) {
    // Each tower has a recognizable footprint even in monochrome.
    if (['bastion','bulwark','siege'].includes(id)) {
      box(-21,-19,42,40,p.dark); box(-17,-16,34,32,p.body);
      for (const x of [-19,13]) box(x,-15,6,30,p.light);
    } else if (['frost','hexer'].includes(id)) {
      poly([-21,-12,0,-24,21,-12,21,12,0,24,-21,12],p.dark);
      poly([-17,-10,0,-19,17,-10,17,10,0,19,-17,10],p.body);
    } else if (id === 'grove') {
      for (const side of [-1,1]) { leaf(side*7,4,side,18); leaf(side*3,-8,side,14); }
      ellipse(0,2,13,13,p.dark); ellipse(0,0,9,10,p.light);
    } else if (['skirmisher','outrider'].includes(id)) {
      poly([0,-24,20,12,0,21,-20,12],p.dark);
      poly([0,-19,14,10,0,15,-14,10],p.body);
    } else {
      ellipse(0,2,21,21,p.dark); ellipse(0,-1,17,17,p.body);
      path('M-14,2 A14,14 0 0,1 7,-13','none',p.light,2.4);
    }
    // Structural progression: twin braces, outriggers, crest, elemental crown.
    if (r >= 2) for (const side of [-1,1]) box(side*16-3,6,6,13,p.light);
    if (r >= 3) for (const side of [-1,1]) poly([side*16,-10,side*27,-17,side*25,6,side*18,10],p.body);
    if (r >= 4) { box(-14,16,28,9,p.dark); gem(0,21,5); for(const s of [-1,1]) gem(s*22,1,4); }
    if (r >= 5) for (const side of [-1,1]) {
      poly([side*19,-14,side*29,-28,side*28,-4,side*23,4],p.light);
      gem(side*24,-15,4);
    }
    const back = recoil * 5;
    // Weapons point north; the battlefield rotates the complete tower toward its target.
    if (id === 'pebble') {
      for(const s of [-1,1]) box(s*12-4,-15,8,29,p.dark);
      line(-13,-15,13,-15,p.light,4); line(0,11,0,-18+back,p.dark,5);
      ellipse(0,-16+back,7,6,p.light); gem(0,10,4);
    } else if(id==='brook') {
      poly([-14,-9,0,-22,14,-9,10,13,0,19,-10,13],p.light);
      path('M-9,1 Q-3,-9 2,-1 Q7,7 10,-3','none',p.body,4);
      box(-4,-26+back,8,14,p.dark);
    } else if(id==='cinder') {
      box(-14,-14,28,29,p.dark);
      for(const s of [-1,1]) { box(s*8-4,-21+back,8,24,p.light); ellipse(s*8,-19+back,3,4,p.ink); }
      flame(0,10,7);
    } else if(id==='tempest') {
      path('M-21,-9 Q0,-28 21,-9','none',p.light,5);
      path(`M-21,-9 L0,${4+back} L21,-9`,'none',p.dark,2);
      poly([0,-28,5,-18,2,12,-2,12,-5,-18],p.light);
      for(const s of [-1,1]) feather(s*6,10,s,10);
    } else if (id === 'pyre') {
      ellipse(0,back,11,11,p.dark); ellipse(0,back,8,8,p.light);
      flame(0,-6+back,10+r);
      if (r >= 3) for(const s of [-1,1]) flame(s*12,3,5);
    } else if (id === 'bombard') {
      box(-13,-13+back,26,29,p.light); ellipse(0,-10+back,11,12,p.dark);
      ellipse(0,-12+back,7,8,p.ink); ellipse(0,-12+back,4,5,p.body);
      for(const s of [-1,1]) { box(s*16-3,-9,6,18,p.dark); gem(s*16,4,3); }
      if(r>=4) flame(0,13,5);
    } else if(id==='frost') {
      for(const s of [-1,1]) poly([s*5,7,s*16,-13,s*8,-23,s*2,-4],p.light);
      poly([0,-29+back,7,-8+back,0,12,-7,-8+back],p.body); gem(0,-9+back,9);
    } else if(id==='hexer') {
      ellipse(0,0,12,12,p.ink); ellipse(0,0,9,9,p.body); gem(0,0,7);
      for(let i=0;i<3;i++) { const a=time*.65+i*Math.PI*2/3; gem(Math.cos(a)*15,Math.sin(a)*15,3); }
    } else if(id==='grove') {
      for(const s of [-1,1]) leaf(s*4,-5,s,13);
      gem(0,-5,7); line(0,7,0,15,p.dark,5);
    } else if(id==='bastion'||id==='bulwark') {
      box(-13,-13,26,28,p.dark); box(-10,-10,20,21,p.light);
      for(const x of [-12,-3,6]) box(x,-18,6,9,p.body);
      if(id==='bulwark') { box(-5,-26+back,10,25,p.dark); box(-7,-26+back,14,7,p.light); }
      else gem(0,1,7);
    } else if(id==='siege') {
      box(-11,-19+back,22,36,p.dark);
      poly([-10,-11+back,0,-28+back,10,-11+back,8,9,-8,9],p.light);
      poly([0,-22+back,5,-10+back,0,2,-5,-10+back],p.body);
      for(const s of [-1,1]) flame(s*12,12,5);
    } else if(id==='banner') {
      line(0,17,0,-23,p.ink,5); gem(0,-22,4);
      const flutter=Math.sin(time*3)*2;
      path(`M2,-19 Q13,${-21+flutter} 23,-14 L19,2 Q10,-4 2,-1 Z`,p.light);
      poly([8,-14,16,-10,8,-6],p.dark,'none');
      feather(-5,5,-1,12); feather(5,10,1,11);
    } else {
      const twin=id==='skirmisher';
      for(const x of twin?[-7,7]:[0]) {
        box(x-4,-25+back,8,30,p.dark); box(x-5,-25+back,10,6,p.light);
      }
      ellipse(0,4,10,9,p.body); gem(0,2,4);
      if(id==='outrider') for(const s of [-1,1]) feather(s*7,8,s,15);
      if(id==='vanguard') poly([-10,8,0,15,10,8,0,20],p.light);
    }
  } else {
    // Overhead bodies: boots behind shoulders, helmet crown above the back.
    const heavy=['bastion','bulwark','siege'].includes(id);
    const swift=['skirmisher','outrider'].includes(id);
    const width=heavy?12:swift?7:9;
    if(id==='siege') {
      // A ram is a vehicle, with a driver visible from above.
      for(const s of [-1,1]) { box(s*15-3,-14,6,31,p.ink); for(let y=-10;y<16;y+=7) line(s*15-2,y+walk,s*15+2,y+walk,p.light,2); }
      box(-12,-14,24,33,p.dark); box(-9,-9,18,23,p.body);
    } else {
      if(prestige>=2||r>=3) {
        const tail=17+(r>=4?5:0);
        poly([-width+1,-2,width-1,-2,width+4,tail,0,tail-4,-width-4,tail],p.dark);
        line(-width,tail-2,0,tail-6,p.light,1.5);
      }
      for(const s of [-1,1]) box(s*5-3,9+s*walk*3,6,9,p.ink);
      ellipse(0,3,width+2,12,p.ink); ellipse(0,1,width,10,p.body);
      path(`M${-width+2},-2 Q-2,-9 ${width-2},-2`,'none',p.light,2);
      for(const s of [-1,1]) ellipse(s*(width+2),2-s*walk*2,3.4,5,p.dark);
    }
    if(r>=2) for(const s of [-1,1]) {
      poly([s*5,-5,s*(width+6),-7,s*(width+6),3,s*8,5],p.light);
      line(s*9,-3,s*(width+3),-3,p.body,2);
    }
    if(r>=3) { box(-width+1,5,width*2-2,6,p.dark); gem(0,7,3); }
    if(r>=4) for(const s of [-1,1]) poly([s*(width+2),-5,s*(width+8),-12,s*(width+6),6,s*(width+2),8],p.body);

    // A compact crown/hood, no front-facing eyes or chest portraits.
    ellipse(0,-7,7,7,p.ink); ellipse(-.6,-8,5.5,5.6,p.light);
    path('M-4,-10 Q0,-14 4,-10','none',p.hot,1.5);
    line(0,-13,0,-5,p.dark,2);

    const thrust=hit*5;
    if(id==='pebble') {
      box(-16,0,8,13,p.dark); ellipse(-12,3,3,3,p.light);
      const sx=17+Math.sin(hit*Math.PI)*5;
      path(`M10,3 Q${sx+8},-9 ${sx},-20 Q${sx-8},-10 10,3`,'none',p.light,2);
      ellipse(sx,-20,4,3,p.dark); ellipse(sx,-21,2.5,2,p.light);
    } else if(id==='brook') {
      poly([-22,-12,-10,-16,-6,-6,-10,10,-20,7],p.light);
      path('M-18,-4 Q-12,-10 -11,-2 Q-12,4 -17,2','none',p.body,2);
      line(13,10,13,-18-thrust,p.dark,3); poly([13,-27-thrust,17,-17-thrust,9,-17-thrust],p.light);
    } else if(id==='cinder') {
      poly([-12,-2,-7,-13,7,-13,13,-2,8,14,-8,14],p.dark);
      for(const s of [-1,1]) line(s*5,-8,s*8,7,p.body,3);
      flame(0,-16,6); gem(0,5,5);
    } else if(id==='tempest') {
      for(const s of [-1,1]) feather(s*6,6,s,10);
      path('M10,-22 Q32,-4 10,16','none',p.light,3);
      path(`M10,-22 L${10-hit*7},-3 L10,16`,'none',p.dark,1.5);
      line(10-hit*7,-2,24,-15,p.hot,2);
    } else if(id==='vanguard') {
      line(13,17,13,-23-thrust,p.ink,4); line(13,14,13,-22-thrust,p.light,1.6);
      poly([13,-31-thrust,18,-21-thrust,13,-17-thrust,8,-21-thrust],p.light);
      poly([-18,-10,-8,-9,-8,5,-13,10,-19,4],p.body); line(-14,-6,-14,4,p.light,2);
      if(r>=3) poly([14,-18-thrust,24,-14-thrust,14,-10-thrust],p.body);
    } else if(id==='pyre') {
      for(const s of [-1,1]) { ellipse(s*12,4,5,6,p.body); flame(s*12,-2-hit*3,4+r*.7); }
      poly([-5,-12,0,-19,5,-12,0,-8],p.dark); flame(0,-14,4+r*.8);
    } else if(id==='frost'||id==='hexer') {
      const x=15;
      path(`M${x-4},-22 Q${x+13+hit*2},-3 ${x-4},16`,'none',p.ink,4);
      path(`M${x-4},-22 Q${x+12+hit*2},-3 ${x-4},16`,'none',p.light,2);
      path(`M${x-4},-22 L${x-4-hit*7},-3 L${x-4},16`,'none',p.hot,1);
      line(x-4-hit*7,-3,x-4-hit*7,-24,p.light,2);
      if(id==='hexer') { poly([-8,-8,0,-21,8,-8,0,-12],p.dark); gem(-12,3,5); }
      else { poly([-7,-9,-10,-17,-2,-13],p.body); gem(0,-8,3); }
    } else if(id==='grove') {
      line(16,18,16,-17-hit*3,p.dark,4);
      leaf(14,-18-hit*3,-1,9); leaf(16,-17-hit*3,1,9); gem(16,-18-hit*3,4);
      for(const s of [-1,1]) leaf(s*5,-7,s,8);
    } else if(id==='bastion'||id==='bulwark') {
      const shield=id==='bulwark'?23:18;
      poly([-shield,-19,0,-23,shield,-19,shield,-9,0,-4,-shield,-9],p.dark);
      poly([-shield+3,-17,0,-20,shield-3,-17,shield-3,-11,0,-7,-shield+3,-11],p.light);
      gem(0,-14,4); if(id==='bulwark') for(const s of [-1,1]) box(s*18-3,-25,6,10,p.body);
    } else if(id==='bombard') {
      ellipse(0,8,13,12,p.ink); ellipse(-1,6,10,9,p.dark);
      path('M-8,3 Q-5,-1 1,-1','none',p.light,2);
      line(4,0,9,-6,p.light,2); flame(10,-9,4+Math.sin(time*8)*.6);
      for(const s of [-1,1]) box(s*12-3,-5,6,9,p.light);
    } else if(id==='skirmisher') {
      for(const s of [-1,1]) poly([s*12,7,s*22,-13-thrust,s*16,-11-thrust,s*9,2],p.light);
      feather(-3,-9,-1,8);
    } else if(id==='outrider') {
      for(const s of [-1,1]) feather(s*8,4,s,12+r);
      line(15,14,15,-22-thrust,p.dark,3); poly([15,-30-thrust,19,-20-thrust,11,-20-thrust],p.hot);
    } else if(id==='banner') {
      line(13,18,13,-24,p.dark,4);
      const wave=Math.sin(time*4)*3;
      path(`M14,-23 Q23,${-23+wave} 29,-18 L27,0 Q21,${-7+wave} 14,-5 Z`,p.light);
      poly([17,-18,24,-14,17,-10],p.dark,'none'); gem(13,-26,3);
    } else if(id==='siege') {
      box(-5,-23-thrust,10,27,p.light);
      poly([-13,-19-thrust,0,-29-thrust,13,-19-thrust,9,-12-thrust,-9,-12-thrust],p.dark);
      poly([-9,-19-thrust,0,-25-thrust,9,-19-thrust,0,-16-thrust],p.body);
      if(r>=3) for(const s of [-1,1]) flame(s*9,12,5);
    }
    if(r>=5) {
      // Element-shaped final silhouette, not a universal gold recolor.
      if(ELEMENT_OF[id]==='fire') for(const s of [-1,1]) flame(s*18,8,6);
      else if(ELEMENT_OF[id]==='wind') for(const s of [-1,1]) feather(s*8,12,s,16);
      else if(ELEMENT_OF[id]==='water') for(const s of [-1,1]) poly([s*10,10,s*23,17,s*18,-1],p.light);
      else for(const s of [-1,1]) poly([s*10,9,s*23,15,s*22,1,s*16,-4],p.light);
      gem(0,-8,4);
    }
  }
  // Rarity is expressed at every rank with fittings in the element's own materials.
  if(prestige>=1) for(const s of [-1,1]) gem(s*(minion?8:12),minion?8:11,2.5);
  if(prestige>=2) for(const s of [-1,1]) poly([s*5,minion?-11:10,s*9,minion?-19:3,s*12,minion?-10:12],p.light);
  if(prestige>=3) { gem(0,minion?1:9,4); for(const s of [-1,1]) line(s*4,minion?13:18,s*11,minion?16:21,p.hot,1.8); }
  return result;
}

// Only static drawings are cached; animated paths are bounded to a small per-frame list.
const STATIC = new Map<string, Piece[]>();
function pieces(id: string, rank: number, minion: boolean, pose?: ArtPose): Piece[] {
  if(pose) return design(id,rank,minion,pose);
  const key=`${id}:${level(rank)}:${minion}`;
  let value=STATIC.get(key); if(!value) { value=design(id,rank,minion); STATIC.set(key,value); }
  return value;
}
const PATHS = new Map<string, Path2D>();
function paint(ctx: CanvasRenderingContext2D, list: Piece[]): void {
  ctx.lineJoin='round'; ctx.lineCap='round';
  for(const shape of list) {
    let path=PATHS.get(shape.d);
    if(!path) {
      path=new Path2D(shape.d); PATHS.set(shape.d,path);
      if(PATHS.size>4096) PATHS.delete(PATHS.keys().next().value!);
    }
    if(shape.fill!=='none') { ctx.fillStyle=shape.fill; ctx.fill(path); }
    if(shape.stroke!=='none') { ctx.strokeStyle=shape.stroke; ctx.lineWidth=shape.width; ctx.stroke(path); }
  }
}
function svg(id: string, px: number, rank: number, minion: boolean): string {
  return `<svg class="${minion?'minion':'tower'}-mark" viewBox="-38 -38 76 76" width="${px}" height="${px}" aria-hidden="true"><g transform="scale(${rankScale(rank)})" stroke-linejoin="round" stroke-linecap="round">${pieces(id,rank,minion).map(s=>`<path d="${s.d}" fill="${s.fill}" stroke="${s.stroke}" stroke-width="${s.width}"/>`).join('')}</g></svg>`;
}
export function towerMarkSvg(id: string, px: number, rank=1): string { return svg(id,px,rank,false); }
export function minionMarkSvg(id: string, px: number, rank=1): string { return svg(id,px,rank,true); }
export function bossMarkSvg(id: string, px: number): string { return svg(id,px,4,true); }
export function drawBossMark(ctx: CanvasRenderingContext2D,id: string,x: number,y: number,size: number,facing=0,pose?: ArtPose): void {
  ctx.save(); ctx.translate(x,y); ctx.rotate(facing); ctx.scale(size/76,size/76);
  paint(ctx,pieces(id,4,true,pose)); ctx.restore();
}
export function drawTowerMark(ctx: CanvasRenderingContext2D,id: string,x: number,y: number,size: number,rank: number,aim=-Math.PI/2,recoil=0,time=0): void {
  const scale=size/76*rankScale(rank);
  ctx.save(); ctx.translate(x,y); ctx.rotate(aim+Math.PI/2); ctx.scale(scale,scale);
  paint(ctx,pieces(id,rank,false,{recoil,time})); ctx.restore();
}
export function drawMinionMark(ctx: CanvasRenderingContext2D,id: string,x: number,y: number,height: number,rank: number,pose?: ArtPose): void {
  const scale=height/64*rankScale(rank);
  ctx.save(); ctx.translate(x,y); ctx.scale(scale,scale);
  paint(ctx,pieces(id,rank,true,pose)); ctx.restore();
}
export function drawElementBadge(ctx: CanvasRenderingContext2D,element: ElementId,x: number,y: number,radius: number): void {
  ctx.save(); ctx.translate(x,y); ctx.fillStyle=ELEMENT_STYLE[element].color;
  ctx.strokeStyle=PALETTES[element].ink; ctx.lineWidth=1.5;
  ctx.beginPath(); ctx.arc(0,0,radius,0,Math.PI*2); ctx.fill(); ctx.stroke();
  ctx.fillStyle=PALETTES[element].ink; ctx.font=`bold ${radius*1.3}px sans-serif`;
  ctx.textAlign='center'; ctx.textBaseline='middle'; ctx.fillText({fire:'F',water:'W',earth:'E',wind:'A'}[element],0,0); ctx.restore();
}
